<template>
<div
  id="tgb-navbar"
  :class="['tgb-navbar', classes]"
>
  <div class="navbar-links-container">
    <div class="nav-icon">
      <launcher
        :open="launcherOpen"
        :toggle-open="toggleOpen"
        :launcher-applications="launcherApplications"
        :launcher-discover-applications="launcherDiscoverApplications"
        :launcher-staff-tools="launcherStaffTools"
        :user="user"
        :click-launcher-application="handleClickLauncherApplication"
      />
    </div>
  </div>
  <div
    class="navbar-brand-container ml-0 mr-4 flex shrink-0 items-center"
    @click="handleClickNavbarBrand"
  >
    <img
      id="navbarBrandIcon"
      class="navbar-brand-icon block"
      alt="Tagboard logo"
      :src="icon"
    />
    <div id="navbarBrandTitle" class="navbar-brand inline-flex items-center leading-none">
      <slot name="title">
        {{ title }}
      </slot>
    </div>
  </div>

  <slot />

  <div class="navbar-links-container navbar-links-left underline">
    <div class="navbar-links">
      <div
        v-for="navLink in navLinks"
        :key="navLink.title"
        :id="navLink.title"
        :class="[
          'nav-link',
          activeTab === navLink.title ? 'active' : '',
        ]"
        @click="handleClickNavigationLink(navLink)"
      >
        {{ navLink.title }}
      </div>
    </div>
  </div>
  <div class="navbar-links-container">
    <div
      v-for="navIcon in navIcons"
      :key="navIcon.id"
      :class="`nav-icon ${navIcon.containerClass}`"
      @click="handleClickNavigationIcon(navIcon)"
    >
      <i
        :id="navIcon.id"
        :class="`tgb-fa-icon ${navIcon.iconClass}`"
      />
    </div>
    <div class="nav-icon">
      <account-dropdown
        :open="accountOpen"
        :toggle-open="toggleOpen"
        :user="user"
        :default-avatar="defaultAvatar"
        :domain="domain"
      />
    </div>
  </div>
</div>
</template>

<script>
import Launcher from './Launcher.vue';
import AccountDropdown from './AccountDropdown.vue';

export default {
  name: 'NavBar',
  components: {
    Launcher,
    AccountDropdown,
  },
  props: {
    icon: {
      type: String,
    },
    title: {
      type: String,
    },
    activeTab: {
      type: String,
    },
    navLinks: {
      type: Array,
    },
    navIcons: {
      type: Array,
    },
    launcherApplications: {
      type: Array,
      required: false,
    },
    launcherDiscoverApplications: {
      type: Array,
      required: false,
    },
    launcherStaffTools: {
      type: Array,
      required: false,
    },
    user: {
      type: Object,
    },
    defaultAvatar: {
      required: true,
    },
    classes: String,
    domain: String,
  },
  data() {
    return {
      launcherOpen: false,
      accountOpen: false,
      windowWidth: window.innerWidth,
      taglandiaUrl: `https://app.${this.domain}`,
    };
  },
  methods: {
    toggleOpen (menuName) {
      this.launcherOpen = menuName === 'launcher' ? this.launcherOpen = !this.launcherOpen : false;
      this.accountOpen = menuName === 'account' ?  this.accountOpen = !this.accountOpen : false;
    },
    handleClickOutside (e) {
      const { target } = e;

      const menuIds = ['applicationLauncherIcon', 'accountDropdownIcon'];

      // checks if the clicked classname matches a menu classname
      const menuClick = menuIds.some(menu => target.id.includes(menu));

      // closes all menus if a menu wasn't clicked
      if (!menuClick) this.toggleOpen('');
    },

    handleClickNavbarBrand() {
      this.$emit('click-navbar-brand');
    },

    handleClickNavigationLink(navLink) {
      this.$emit('click-navbar-link', navLink.title);
    },

    handleClickNavigationIcon(navIcon) {
      this.$emit('click-navbar-icon', navIcon);
    },

    handleClickLauncherApplication(application) {
      this.$emit('click-launcher-application', application);
    },

    handleResize() {
      this.windowWidth = window.innerWidth;
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('resize', this.handleResize);
  },
  computed: {
    mobile() {
      return this.windowWidth <= 767;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './NavbarVars';
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500&display=swap');

$font-family-sans-serif: "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";

.tgb-navbar,
.navbar-brand-container,
.navbar-links-container,
.navbar-account-container {
  display: flex;
  align-items: center;
}

.tgb-navbar {
  padding: 0 0.9375rem;
  height: 4rem;
  box-sizing: border-box;
  color: $tgb-primary-font;
  background: $white;
  align-items: center;

  .navbar-brand-container {
    margin-right: 1rem;

    .navbar-brand-icon {
      height: 1.875rem;
      border-radius: $tgb-border-radius;

      &:hover {
        cursor: pointer;
      }
    }

    .navbar-brand {
      font-size: 0.8125rem;
      font-weight: 500;
      white-space: nowrap;
      margin: 0 0.5rem;
      letter-spacing: .16em;
      color: $tgb-primary-font;
      text-transform: uppercase;
      display: flex;
      align-items: center;

      &:hover {
        cursor: pointer;
      }
    }
  }

  .navbar-links-container {
    line-height: 4rem;

    .navbar-links {
      display: flex;
      flex-direction: row;
      align-items: center;
      font-size: 0.9em;
    }

    .nav-link {
      padding: 0 0.9375rem;
      transition: all 250ms ease-in-out;
      border-bottom: 1px solid transparent;
    }

    .nav-link.active,
    .nav-link:hover {
      color: $tgb-primary-accent;
      cursor: pointer;
    }

    &.underline .nav-link:hover,
    &.underline .nav-link.active {
      border-bottom: 1px solid #2bacca;
    }

    .nav-icon {
      margin: 0;
      font-size: 1.125rem;
      color: $tgb-secondary-font;

      &:hover {
        cursor: pointer;

        .tgb-fa-icon {
          color: $tgb-primary-accent;
          background-color: $tgb-primary-accent-hover-light;
        }
      }

      &:last-child { // kills the right margin for the user's account image
        margin-right: 0;
      }

      .tgb-fa-icon {
        transition: all 250ms ease-in-out;
        border-radius: $tgb-border-radius;
        padding: 0.625rem;
        background-color: transparent;
      }
    }
  }

  .navbar-links-left {
    flex-grow: 1;
  }

  &.dark {
    background: $tgb-navbar-dark;
    color: $tgb-primary-font-dark;

    .navbar-brand {
      color: $tgb-primary-font-dark
    }

    .nav-icon {
      color: $tgb-primary-icon-dark;
    }

    .nav-icon {
      &:hover {
        .tgb-fa-icon {
          color: $tgb-primary-font-dark;
          background-color: $tgb-primary-icon-hover-dark;
        }
      }
    }
  }

  &.dark-alt {
    background: $tgb-navbar-dark-alt;
    color: $tgb-primary-font-dark;

    .navbar-brand {
      color: $tgb-primary-font-dark
    }

    .nav-icon {
      color: $tgb-primary-icon-dark;
    }

    .nav-icon {
      &:hover {
        .tgb-fa-icon {
          color: $tgb-primary-font-dark;
          background-color: $tgb-primary-icon-hover-dark-alt;
        }
      }
    }
  }

  .taglandia-link {
      background: linear-gradient(45deg, rgba(20,169,188,1) 0%, rgba(18,174,194,1) 20%, rgba(161,69,164,1) 56%, rgba(161,69,164,1) 81%, rgba(204,0,127,1) 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      cursor: pointer;
      font-weight: bold;
    }
}
</style>
